import { motion, useReducedMotion } from 'motion/react';
import { type CSSProperties, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentPanelComposer } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentPanelComposer';
import { PrivateWorkspaceAgentPanelComposerArchivedSessionDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentPanelComposerArchivedSessionDock';
import { PrivateWorkspaceAgentPanelComposerCreditsDepletedDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentPanelComposerCreditsDepletedDock';
import { PrivateWorkspaceAgentPanelBinaryChoiceDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/binary-choice/PrivateWorkspaceAgentPanelBinaryChoiceDock';
import { PrivateWorkspaceAgentPanelFormDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/PrivateWorkspaceAgentPanelFormDock';
import { PrivateWorkspaceAgentPanelSelectDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/PrivateWorkspaceAgentPanelSelectDock';
import { PrivateWorkspaceAgentPanelToolConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/tool-consent/PrivateWorkspaceAgentPanelToolConsentDock';
import { PrivateWorkspaceAgentPanelUnknownConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/unknown/PrivateWorkspaceAgentPanelUnknownConsentDock';
import { PrivateWorkspaceAgentPanelMessageList } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageList';
import {
  PrivateWorkspaceAgentPanelChatDropZone,
  type PrivateWorkspaceAgentPanelChatDropZoneRenderProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelChatDropZone';
import { PrivateWorkspaceAgentPanelHeader } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelHeader';
import { PrivateWorkspaceAgentPanelMyTasksView } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelMyTasksView';
import { PrivateWorkspaceAgentPanelNavOverlayDimmer } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentPanelNavOverlayDimmer';
import { WorkspaceAgentPanelBodyElementProvider } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelBodyElementContext';
import { WorkspaceAgentPanelDockedResizer } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelDockedResizer';
import { WorkspaceAgentPanelFloatResizeHandles } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelFloatResizeHandles';
import { WorkspaceAgentPanelSnapToRightGuide } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelSnapToRightGuide';
import {
  workspaceAgentComposerConsentSlot,
  workspaceAgentComposerDockGrid,
  workspaceAgentComposerDockOverlay,
  workspaceAgentComposerDockStackCell,
  workspaceAgentComposerIdleSlot,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import {
  getWorkspaceAgentPanelViewMotionTransition,
  workspaceAgentPanelViewTransitionVariants,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelViewTransition';
import { WORKSPACE_AGENT_TASKS_TOGGLE_FOCUS_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentTasksToggleFocusAttribute';
import { useIsActiveWorkspaceAgentSessionArchived } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsActiveWorkspaceAgentSessionArchived';
import { useWorkspaceAgentComposerSlotReservedHeight } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentComposerSlotReservedHeight';
import { useWorkspaceAgentPanelFloatInteraction } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelFloatInteraction';
import { type WorkspaceAgentPanelSurface } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelViewport';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { isAskFormElicitationEntry } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/isAskFormElicitationEntry';
import { isBinaryChoiceElicitationEntry } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/isBinaryChoiceElicitationEntry';
import { isSelectElicitationEntry } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/isSelectElicitationEntry';
import { isToolConsentElicitationEntry } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/isToolConsentElicitationEntry';
import { getPendingConsentDockElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getPendingConsentDockElicitation';
import {
  WORKSPACE_AGENT_PANEL_BODY_ELEMENT_ID,
  WORKSPACE_AGENT_PANEL_ELEMENT_ID,
} from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';

type PrivateWorkspaceAgentPanelConsentDockProps = {
  onDeny: () => void;
  pendingConsentDockElicitation: ReturnType<typeof getPendingConsentDockElicitation>;
};

const PrivateWorkspaceAgentPanelConsentDock = memo(
  ({ onDeny, pendingConsentDockElicitation }: PrivateWorkspaceAgentPanelConsentDockProps) => {
    if (!pendingConsentDockElicitation) {
      return null;
    }

    if (isBinaryChoiceElicitationEntry(pendingConsentDockElicitation)) {
      return (
        <PrivateWorkspaceAgentPanelBinaryChoiceDock
          elicitation={pendingConsentDockElicitation}
          key={pendingConsentDockElicitation.id}
        />
      );
    }

    if (isSelectElicitationEntry(pendingConsentDockElicitation)) {
      return (
        <PrivateWorkspaceAgentPanelSelectDock
          elicitation={pendingConsentDockElicitation}
          key={pendingConsentDockElicitation.id}
        />
      );
    }

    if (isAskFormElicitationEntry(pendingConsentDockElicitation)) {
      return (
        <PrivateWorkspaceAgentPanelFormDock
          elicitation={pendingConsentDockElicitation}
          key={pendingConsentDockElicitation.id}
        />
      );
    }

    if (isToolConsentElicitationEntry(pendingConsentDockElicitation)) {
      return (
        <PrivateWorkspaceAgentPanelToolConsentDock
          elicitation={pendingConsentDockElicitation}
          key={pendingConsentDockElicitation.id}
          onCancel={onDeny}
        />
      );
    }

    return (
      <PrivateWorkspaceAgentPanelUnknownConsentDock
        elicitation={pendingConsentDockElicitation}
        key={pendingConsentDockElicitation.id}
        onCancel={onDeny}
      />
    );
  },
);

PrivateWorkspaceAgentPanelConsentDock.displayName = 'PrivateWorkspaceAgentPanelConsentDock';

type PrivateWorkspaceAgentPanelChatViewProps = {
  error: string | null;
  pendingElicitations: ReturnType<typeof usePrivateWorkspaceAgentPanelSession>['pendingElicitations'];
  respondToElicitation: ReturnType<typeof usePrivateWorkspaceAgentPanelSession>['respondToElicitation'];
};

const PrivateWorkspaceAgentPanelChatView = memo(
  ({ error, pendingElicitations, respondToElicitation }: PrivateWorkspaceAgentPanelChatViewProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const { canConsumeMoreCredits } = useCanConsumeMoreCredits();
    const { isActiveSessionArchived, isActiveSessionArchiveStatusPending } = useIsActiveWorkspaceAgentSessionArchived();
    const composerSizerRef = useRef<HTMLDivElement | null>(null);
    const isArchivedSessionDockOpen = isActiveSessionArchived && !session.isComposerRunLocked;
    const isCreditsDepletedDockOpen =
      !canConsumeMoreCredits && !session.isComposerRunLocked && !isArchivedSessionDockOpen;
    const panelError = isCreditsDepletedDockOpen || isArchivedSessionDockOpen ? null : error;
    const pendingConsentDockElicitation = useMemo(
      () => getPendingConsentDockElicitation(pendingElicitations),
      [pendingElicitations],
    );

    const handleDenyConsentDock = useCallback(() => {
      if (!pendingConsentDockElicitation) {
        return;
      }

      void respondToElicitation({
        elicitationId: pendingConsentDockElicitation.id,
        response: 'denied',
      });
    }, [pendingConsentDockElicitation, respondToElicitation]);

    const isConsentDockOpen = pendingConsentDockElicitation !== null;
    const isChoiceDockOpen =
      pendingConsentDockElicitation !== null &&
      (isBinaryChoiceElicitationEntry(pendingConsentDockElicitation) ||
        isSelectElicitationEntry(pendingConsentDockElicitation) ||
        isAskFormElicitationEntry(pendingConsentDockElicitation));
    const isComposerHiddenForDockOverlay = isConsentDockOpen || isCreditsDepletedDockOpen;
    const { composerSlotMinHeight, composerSlotRef } = useWorkspaceAgentComposerSlotReservedHeight({
      isConsentDockOpen,
    });

    const setComposerSizerRef = useCallback(
      (node: HTMLDivElement | null) => {
        composerSizerRef.current = node;
        if (node) {
          node.inert = isComposerHiddenForDockOverlay;
        }
      },
      [isComposerHiddenForDockOverlay],
    );

    const isComposerDisplayed = !isArchivedSessionDockOpen && !isComposerHiddenForDockOverlay;
    const isComposerEnabled =
      !session.isComposerRunLocked &&
      canConsumeMoreCredits &&
      !isActiveSessionArchived &&
      !isActiveSessionArchiveStatusPending;

    const renderChatDropZoneChildren = useCallback(
      ({ isSuppressingEditorAgentDrag, onComposerEditorChange }: PrivateWorkspaceAgentPanelChatDropZoneRenderProps) => (
        <>
          <PrivateWorkspaceAgentPanelMessageList />

          {/* Consent is chat-only and sits in the composer slot so the thread shrinks instead of
            sitting behind a scrim. My Tasks never mounts this column. `max-h-[70%]` keeps at
            least ~30% of the thread visible for context while a tall dock is open. The dock is a
            full-bleed sheet, so the slot drops its inset while one is open. The inner grid uses
            `grow` rather than `min-h-full` so a short dock hugs its content and an error banner
            above it does not push Cancel / Continue past the panel clip. */}
          <div
            className={isConsentDockOpen ? workspaceAgentComposerConsentSlot() : workspaceAgentComposerIdleSlot()}
            data-testid="WORKSPACE_AGENT_COMPOSER_SLOT"
            ref={composerSlotRef}
            style={composerSlotMinHeight > 0 ? { minHeight: composerSlotMinHeight } : undefined}
          >
            {panelError ? (
              <p
                className={cn(
                  'mb-2 shrink-0 rounded-6 border ads:border-error border-flash-4 ads:bg-error-subtle bg-flash-2 px-2 py-1 ads:text-error text-12 text-flash-11',
                  isConsentDockOpen && 'mx-3',
                )}
                data-testid="WORKSPACE_AGENT_PANEL_ERROR"
                role="alert"
              >
                {panelError}
              </p>
            ) : null}
            {isArchivedSessionDockOpen && !isConsentDockOpen ? (
              <PrivateWorkspaceAgentPanelComposerArchivedSessionDock />
            ) : (
              <>
                {/* Prototype `FloatingComposerDock`: the composer stays mounted as the dock's sizer, so the dock
                  covers the input's footprint instead of replacing it with a taller card. The grid stacks
                  both in one cell; the dock only grows past the composer for tall display content. */}
                <div className={workspaceAgentComposerDockGrid({ isConsentDockOpen })}>
                  <div
                    className={cn(
                      workspaceAgentComposerDockStackCell(),
                      isChoiceDockOpen && 'hidden',
                      isComposerHiddenForDockOverlay && !isChoiceDockOpen && 'invisible',
                    )}
                    ref={setComposerSizerRef}
                  >
                    <PrivateWorkspaceAgentPanelComposer
                      isSuppressingEditorAgentDrag={isSuppressingEditorAgentDrag}
                      onEditorChange={onComposerEditorChange}
                    />
                  </div>
                  {isConsentDockOpen ? (
                    <div className={workspaceAgentComposerDockOverlay()}>
                      <PrivateWorkspaceAgentPanelConsentDock
                        onDeny={handleDenyConsentDock}
                        pendingConsentDockElicitation={pendingConsentDockElicitation}
                      />
                    </div>
                  ) : null}
                  {isCreditsDepletedDockOpen && !isConsentDockOpen ? (
                    <div className={workspaceAgentComposerDockOverlay()}>
                      <PrivateWorkspaceAgentPanelComposerCreditsDepletedDock />
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </>
      ),
      [
        composerSlotMinHeight,
        composerSlotRef,
        handleDenyConsentDock,
        isArchivedSessionDockOpen,
        isChoiceDockOpen,
        isComposerHiddenForDockOverlay,
        isConsentDockOpen,
        isCreditsDepletedDockOpen,
        panelError,
        pendingConsentDockElicitation,
        setComposerSizerRef,
      ],
    );

    return (
      <PrivateWorkspaceAgentPanelChatDropZone
        isComposerDisplayed={isComposerDisplayed}
        isComposerEnabled={isComposerEnabled}
      >
        {renderChatDropZoneChildren}
      </PrivateWorkspaceAgentPanelChatDropZone>
    );
  },
);

PrivateWorkspaceAgentPanelChatView.displayName = 'PrivateWorkspaceAgentPanelChatView';

export type PrivateWorkspaceAgentPanelRootProps = {
  /**
   * When true the card lives inside the spacer-sized overflow clip, so it is in-flow in that clip
   * rather than viewport-fixed. Fixed positioning here would escape the clip.
   */
  isSlotClipped: boolean;
  /**
   * The panel's viewport-fixed rect, from `useWorkspaceAgentPanelViewport`. The panel is out of
   * flow wherever it renders, so its geometry is always supplied rather than derived here.
   */
  style: CSSProperties;
  /** Which surface the panel is on, from that same hook. */
  surface: WorkspaceAgentPanelSurface;
};

export const PrivateWorkspaceAgentPanelRoot = memo(
  ({ isSlotClipped, style, surface }: PrivateWorkspaceAgentPanelRootProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const panelRef = useRef<HTMLElement>(null);
    const [panelBodyElement, setPanelBodyElement] = useState<HTMLDivElement | null>(null);
    // Set by the asset view while it is mounted and cleared on its unmount, so a value here means
    // the panel is sitting on the asset view's surface and should read as part of it.
    const surfaceTheme = useWorkspaceAgentPanelStore((store) => store.surfaceTheme);
    const panelView = useWorkspaceAgentPanelStore((store) => store.panelView);
    const dock = useWorkspaceAgentPanelStore((store) => store.dock);
    const isElevatedAboveOnboarding = useWorkspaceAgentPanelStore((store) => store.isElevatedAboveOnboarding);
    const setPanelView = useWorkspaceAgentPanelStore((store) => store.setPanelView);
    const pendingFocus = useWorkspaceAgentPanelStore((store) => store.pendingFocus);
    const clearPendingFocus = useWorkspaceAgentPanelStore((store) => store.clearPendingFocus);
    const isChatView = panelView === 'chat';
    const isFloating = dock === 'float';
    const { isDragging, onHeaderPointerDown, onResizePointerDown, showSnapToRightGuide } =
      useWorkspaceAgentPanelFloatInteraction({
        panelRef,
      });
    const hasPlayedPanelViewEnterRef = useRef<boolean>();
    // First mount is the slot open; play the chat↔tasks slide only on later view switches.
    const viewEnterInitial = hasPlayedPanelViewEnterRef.current
      ? workspaceAgentPanelViewTransitionVariants[panelView].initial
      : false;
    const shouldReduceMotion = useReducedMotion();
    const viewTransition = getWorkspaceAgentPanelViewMotionTransition(shouldReduceMotion === true);

    useIsomorphicLayoutEffect(() => {
      hasPlayedPanelViewEnterRef.current = true;
    }, []);

    useIsomorphicLayoutEffect(() => {
      if (pendingFocus !== 'panel') {
        return;
      }

      const focusTarget =
        panelView === 'tasks'
          ? panelRef.current?.querySelector<HTMLElement>(`[${WORKSPACE_AGENT_TASKS_TOGGLE_FOCUS_ATTRIBUTE}]`)
          : panelRef.current?.querySelector<HTMLElement>('header button');

      if (focusTarget) {
        focusTarget.focus();
      } else {
        panelRef.current?.focus();
      }

      clearPendingFocus();
    }, [clearPendingFocus, panelView, pendingFocus]);

    return (
      <section
        aria-label="Workspace agent"
        // `z-1` rather than `z-portal`: from its body-level portal this already clears the asset
        // modal, which carries no z-index, while staying under the `z-portal` layer that dropdowns,
        // tooltips and toasts share — those have to keep opening over the panel.
        className={cn(
          'flex min-h-0 flex-col ads:bg-base bg-gray-1 outline-none',
          isSlotClipped ? 'relative h-full' : 'fixed',
          !isSlotClipped && (isElevatedAboveOnboarding ? 'z-above-portal' : 'z-1'),
          // Each surface has its own radius: over the asset view the panel is one of the modal's
          // panels visually and takes the `rounded-12` its siblings in `AssetModalDesktopLayout` all
          // use. On workspace, `rounded-16` matches the pre-ui2026 layout card; ui2026 chrome is
          // `rounded-12`, so the panel matches that when the flag is on.
          surface === 'asset-view' ? 'rounded-12' : 'rounded-16 ui2026:rounded-12',
          // Disconnected shell after leaving the in-flow dock — elevation only (`shadow-popover`).
          isFloating && 'shadow-popover',
        )}
        data-testid="PRIVATE_WORKSPACE_AGENT_PANEL"
        data-theme={surfaceTheme ?? undefined}
        id={WORKSPACE_AGENT_PANEL_ELEMENT_ID}
        ref={panelRef}
        style={
          isElevatedAboveOnboarding
            ? {
                ...style,
                visibility: 'visible',
              }
            : style
        }
        tabIndex={-1}
      >
        {/* Overflow lives on the inner shell so corner/edge resize hits on the outer card stay
          clickable. `overflow-hidden` on the same node as the handles clipped them.
          `relative` is the dimmer's containing block: the outer card is also positioned, and an
          absolute fade anchored there paints the square corner wedges outside this radius. */}
        <div
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[inherit]"
          id={WORKSPACE_AGENT_PANEL_BODY_ELEMENT_ID}
          ref={setPanelBodyElement}
        >
          <WorkspaceAgentPanelBodyElementProvider element={panelBodyElement}>
            <PrivateWorkspaceAgentPanelHeader
              isDragging={isDragging}
              isFloating={isFloating}
              onHeaderPointerDown={onHeaderPointerDown}
              onPanelViewChange={setPanelView}
              panelView={panelView}
            />

            {/* Re-keyed on `panelView` so the entering view — and its zero-state pill stagger — remounts. */}
            <motion.div
              animate={workspaceAgentPanelViewTransitionVariants[panelView].animate}
              className="flex min-h-0 flex-1 flex-col"
              initial={viewEnterInitial}
              key={panelView}
              transition={viewTransition}
            >
              {isChatView ? (
                <PrivateWorkspaceAgentPanelChatView
                  error={session.error}
                  pendingElicitations={session.pendingElicitations}
                  respondToElicitation={session.respondToElicitation}
                />
              ) : (
                <PrivateWorkspaceAgentPanelMyTasksView />
              )}
            </motion.div>
            <PrivateWorkspaceAgentPanelNavOverlayDimmer />
          </WorkspaceAgentPanelBodyElementProvider>
        </div>
        {isFloating ? (
          <WorkspaceAgentPanelFloatResizeHandles onResizePointerDown={onResizePointerDown} />
        ) : (
          <WorkspaceAgentPanelDockedResizer />
        )}
        {showSnapToRightGuide ? <WorkspaceAgentPanelSnapToRightGuide /> : null}
      </section>
    );
  },
);

PrivateWorkspaceAgentPanelRoot.displayName = 'PrivateWorkspaceAgentPanelRoot';
